import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { ReligionType, ReligionIntent } from './useReligionSettings';

export interface ReligionProfile {
  id: string;
  name: string;
  age: number | null;
  bio: string | null;
  photo_url: string | null;
  location: string | null;
  is_verified: boolean;
  has_intro_video: boolean;
  video_url: string | null;
  photos: string[];
  religion: ReligionType;
  intent: ReligionIntent;
  wali_note?: string | null;
}

export function useReligionDiscovery(religion: ReligionType, intent?: ReligionIntent) {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState<ReligionProfile[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchProfiles = useCallback(async () => {
    if (!user || !religion) return;

    setLoading(true);
    try {
      // Get users who have the same religion and are enabled
      const { data: religionUsers, error: religionError } = await supabase
        .from('religion_settings')
        .select('user_id, religion, intent, wali_note')
        .eq('religion', religion)
        .eq('is_enabled', true)
        .neq('user_id', user.id);

      if (religionError) throw religionError;
      if (!religionUsers || religionUsers.length === 0) {
        setProfiles([]);
        setLoading(false);
        return;
      }

      const userIds = religionUsers.map(u => u.user_id);

      // Get already liked/popped users
      const { data: likedUsers } = await supabase
        .from('religion_likes')
        .select('liked_id')
        .eq('liker_id', user.id)
        .eq('religion', religion);

      const { data: poppedUsers } = await supabase
        .from('religion_pops')
        .select('popped_id')
        .eq('popper_id', user.id)
        .eq('religion', religion);

      const excludeIds = new Set([
        ...(likedUsers?.map(l => l.liked_id) || []),
        ...(poppedUsers?.map(p => p.popped_id) || [])
      ]);

      // Filter out already interacted users
      const eligibleUserIds = userIds.filter(id => !excludeIds.has(id));

      if (eligibleUserIds.length === 0) {
        setProfiles([]);
        setLoading(false);
        return;
      }

      // Fetch profiles
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .in('id', eligibleUserIds);

      if (profilesError) throw profilesError;

      // Fetch photos for all profiles
      const { data: photosData } = await supabase
        .from('profile_photos')
        .select('user_id, storage_path')
        .in('user_id', eligibleUserIds)
        .order('position', { ascending: true });

      // Create a map of user photos
      const photosByUser = new Map<string, string[]>();
      photosData?.forEach(photo => {
        const existing = photosByUser.get(photo.user_id) || [];
        existing.push(photo.storage_path);
        photosByUser.set(photo.user_id, existing);
      });

      // Map religion settings by user
      const religionSettingsByUser = new Map(
        religionUsers.map(r => [r.user_id, r])
      );

      // Combine data
      const combinedProfiles: ReligionProfile[] = (profilesData || []).map(profile => {
        const religionSetting = religionSettingsByUser.get(profile.id);
        return {
          id: profile.id,
          name: profile.name,
          age: profile.age,
          bio: profile.bio,
          photo_url: profile.photo_url,
          location: profile.location,
          is_verified: profile.is_verified || false,
          has_intro_video: profile.has_intro_video || false,
          video_url: profile.video_url,
          photos: photosByUser.get(profile.id) || [],
          religion: religionSetting?.religion as ReligionType,
          intent: religionSetting?.intent as ReligionIntent,
          wali_note: religionSetting?.wali_note
        };
      });

      // Filter by intent if specified (for Muslim marriage mode)
      let filteredProfiles = combinedProfiles;
      if (intent === 'marriage' && religion === 'muslim') {
        filteredProfiles = combinedProfiles.filter(p => p.intent === 'marriage');
      }

      setProfiles(filteredProfiles);
    } catch (error) {
      console.error('Error fetching religion profiles:', error);
    } finally {
      setLoading(false);
    }
  }, [user, religion, intent]);

  const likeProfile = async (likedId: string) => {
    if (!user) return { error: new Error('Not authenticated') };

    try {
      const { error } = await supabase
        .from('religion_likes')
        .insert({
          liker_id: user.id,
          liked_id: likedId,
          religion,
          intent
        });

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error liking profile:', error);
      return { error };
    }
  };

  const popProfile = async (poppedId: string) => {
    if (!user) return { error: null, isMatch: false };

    try {
      // Check for mutual pop first
      const { data: mutualPop } = await supabase
        .from('religion_pops')
        .select('id')
        .eq('popper_id', poppedId)
        .eq('popped_id', user.id)
        .eq('religion', religion)
        .single();

      const { error } = await supabase
        .from('religion_pops')
        .insert({
          popper_id: user.id,
          popped_id: poppedId,
          religion,
          intent
        });

      if (error) throw error;
      
      return { error: null, isMatch: !!mutualPop };
    } catch (error) {
      console.error('Error popping profile:', error);
      return { error, isMatch: false };
    }
  };

  const reportProfile = async (
    reportedId: string,
    reason: 'disrespectful_behavior' | 'faith_misrepresentation' | 'intent_misrepresentation' | 'harassment' | 'inappropriate_content',
    details?: string
  ) => {
    if (!user) return { error: new Error('Not authenticated') };

    try {
      const { error } = await supabase
        .from('religion_reports')
        .insert({
          reporter_id: user.id,
          reported_id: reportedId,
          religion,
          reason,
          details
        });

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error reporting profile:', error);
      return { error };
    }
  };

  return {
    profiles,
    loading,
    fetchProfiles,
    likeProfile,
    popProfile,
    reportProfile
  };
}
